import { wait } from '@testing-library/user-event/dist/utils';
import * as bootstrap5 from 'bootstrap-5';
import React from 'react';
import ReactDOM from 'react-dom';

import { analytics } from './analytics/analytics';
import { appConfig } from './config';
import { PackedModalProps } from './Modal';
import { useModalsStaticContext } from './ModalsContext';
import { PackedOffcanvasProps } from './Offcanvas';
import { addStoredMinimizedPopup } from './popup/popupHelper';
import { createUUID } from './util';

type ShowModalProps = Omit<PackedModalProps, 'id'> & { id?: string };
type ShowOffcanvasProps = Omit<PackedOffcanvasProps, 'id'> & { id?: string };

export const useModals = () => {
  const { setIds, setModals, setOffcanvasses } = useModalsStaticContext();

  // Uso questo trigger per anticipare lo useEffect in caso di chiusira di una modale,
  // al fine di aprire il modale precedente "mentre" si chiude l'attuale, non dopo.
  // const [trigger, setTrigger] = React.useState(0);

  const getElementIdByRef = React.useCallback((ref: React.RefObject<HTMLDivElement>) => {
    if (!ref.current) {
      throw new Error('Modal/Offcanvas with ref does not exists.');
    }
    return ref.current.id;
  }, []);

  const show = React.useCallback(
    (modalOrOffcanvas: ShowModalProps | ShowOffcanvasProps, type: 'modal' | 'offcanvas') => {
      const id = modalOrOffcanvas.id || createUUID();

      const handlePostStateChange = () => {
        const element = getElementById(id);
        element.addEventListener(`hide.bs.${type}`, () => {
          element.dataset.closing = 'true';
          // setTrigger((prev) => prev + 1);
        });
        element.addEventListener(`hidden.bs.${type}`, () => {
          delete element.dataset.closing;
          if (element.dataset.keepForLater !== 'true') {
            const instance = getInstanceById(id);
            instance && instance.dispose();
            setIds((prevIds) => prevIds.filter((prevId) => prevId !== id));
            switch (type) {
              case 'modal':
                setModals((prevModals) => prevModals.filter((prevModal) => prevModal.id !== id));
                break;
              case 'offcanvas':
                setOffcanvasses((prevOffcanvasses) =>
                  prevOffcanvasses.filter((prevOffcanvas) => prevOffcanvas.id !== id)
                );
                break;
            }
          }
        });
      };

      switch (type) {
        case 'modal': {
          const modal = modalOrOffcanvas as PackedModalProps;
          // setModals((prevModals) => prevModals.concat({ ...modal, id: id })).then(() => {
          //   handlePostStateChange();
          // });
          ReactDOM.flushSync(() => {
            setModals((prevModals) => {
              // Se la modale è già stata aggiunta non la riaggiungo
              if (prevModals.some((m) => m.id === id)) {
                return prevModals;
              }
              return [...prevModals, { ...modal, id }];
            });
          });
          wait(500).then(() => {
            handlePostStateChange();
          });
          break;
        }
        case 'offcanvas':
          const offcanvas = modalOrOffcanvas as PackedOffcanvasProps;
          // setOffcanvasses((prevOffcanvasses) => prevOffcanvasses.concat({ ...offcanvas, id: id })).then(() => {
          //   handlePostStateChange();
          // });
          ReactDOM.flushSync(() => {
            setOffcanvasses((prevOffcanvasses) => {
              // Se l'offcanvas è già stato aggiunto non lo riaggiungo
              if (prevOffcanvasses.some((o) => o.id === id)) {
                return prevOffcanvasses;
              }
              return [...prevOffcanvasses, { ...offcanvas, id }];
            });
          });
          wait(500).then(() => {
            handlePostStateChange();
          });
          break;
      }

      setIds((prevIds) => {
        // Se l'id è già stato aggiunto non lo riaggiungo, questo serve sopratutto per il minimizzato perché se torno sulla pagina che apre la modale ma è minimizzata non la devo mostrare
        if (prevIds.includes(id)) {
          return prevIds;
        }
        return [...prevIds, id];
      });

      return id;
    },
    [setIds, setModals, setOffcanvasses]
  );

  const showModal = React.useCallback((modal: ShowModalProps) => show(modal, 'modal'), [show]);

  const showOffcanvas = React.useCallback((offcanvas: ShowOffcanvasProps) => show(offcanvas, 'offcanvas'), [show]);

  const showConfirm = React.useCallback(
    ({
      cancelLabel = 'No',
      confirmLabel = 'Yes',
      ...otherModalProps
    }: Omit<PackedModalProps, 'id' | 'buttons' | 'autoClose' | 'closable' | 'onClose'> & {
      cancelLabel?: string;
      confirmLabel?: string;
    }): Promise<void> =>
      new Promise<void>((resolve, reject) => {
        show(
          {
            ...otherModalProps,
            buttons: [
              {
                close: true,
                label: cancelLabel,
                onClick: () => {
                  reject();
                },
                variant: appConfig.form?.buttons.cancel?.defaultVariant,
              },
              {
                close: true,
                label: confirmLabel,
                onClick: () => {
                  resolve();
                },
                variant: appConfig.form?.buttons.submit?.defaultVariant,
              },
            ],
            closable: false,
          } as Omit<PackedModalProps, 'id'>,
          'modal'
        );
      }),
    [show]
  );

  // const equalHeightContext = useEqualHeightContextStrict();

  const showByRef = React.useCallback(
    (ref: React.RefObject<HTMLDivElement>, type: 'modal' | 'offcanvas') => {
      const id = getElementIdByRef(ref);
      // setIds((prevIds) => prevIds.concat(id)).then(() => {
      //   const element = getElementById(id);
      //   element.addEventListener(`hide.bs.${type}`, () => {
      //     element.dataset.closing = 'true';
      //     setTrigger((prev) => prev + 1);
      //   });
      //   element.addEventListener(`hidden.bs.${type}`, () => {
      //     delete element.dataset.closing;
      //     if (element.dataset.keepForLater !== 'true') {
      //       const instance = getInstanceById(id);
      //       instance && instance.dispose();
      //       setIds((prevIds) => prevIds.filter((prevId) => prevId !== id));
      //     }
      //   });
      // });
      ReactDOM.flushSync(() => {
        setIds((prevIds) => {
          if (prevIds.includes(id)) {
            return prevIds;
          }
          return [...prevIds, id];
        });
      });
      // if (equalHeightContext) {
      //   equalHeightContext.updateTrigger();
      // }
      wait(500).then(() => {
        const element = getElementById(id);
        element.addEventListener(`hide.bs.${type}`, () => {
          element.dataset.closing = 'true';
          // setTrigger((prev) => prev + 1);
        });
        element.addEventListener(`hidden.bs.${type}`, () => {
          delete element.dataset.closing;
          if (element.dataset.keepForLater !== 'true') {
            const instance = getInstanceById(id);
            instance && instance.dispose();
            setIds((prevIds) => prevIds.filter((prevId) => prevId !== id));
          }
        });
      });
    },
    [getElementIdByRef, setIds]
  );

  const addEventListener = React.useCallback(
    (
      id: string,
      event: 'show' | 'shown' | 'hide' | 'hidden' | 'hidePrevented',
      type: 'modal' | 'offcanvas',
      callback: () => void,
      alsoWhenKeepingForLater = false
    ) => {
      if (event === 'hidePrevented' && type === 'offcanvas') {
        throw new Error("Event 'hidePrevented' not supported on offcanvas");
      }
      const element = getElementById(id);

      const eventId = `${event}.bs.${type}`;
      const listener = () => {
        if (alsoWhenKeepingForLater || element.dataset.keepForLater !== 'true') {
          callback();
        }
      };

      element.addEventListener(eventId, listener);

      const dispose = () => {
        element.removeEventListener(eventId, listener);
      };

      return dispose;
    },
    []
  );

  const close = React.useCallback(
    (id: string) =>
      new Promise<void>((resolve) => {
        const element = getElementById(id);
        addEventListener(id, 'hidden', getType(element), () => {
          resolve();
        });
        element.dataset.keepForLater = 'false';
        const instance = getInstanceById(id);
        instance && instance.hide();
      }),
    [addEventListener]
  );

  const closeByRef = React.useCallback(
    (ref: React.RefObject<HTMLDivElement>) => {
      const id = getElementIdByRef(ref);
      return close(id);
    },
    [close, getElementIdByRef]
  );

  const minimize = React.useCallback(
    (id: string) => {
      const element = getElementById(id);
      element.dataset.keepForLater = 'true';
      const instance = getInstanceById(id);
      instance && instance.hide();
      // Non è una best practice, migliorare se possibile, ma non volevo mettere una referenza a modals per recuperare il code del popup
      setModals((prev) => {
        const modal = prev.find((m) => m.id === id);
        if (modal && !modal.isMinimized) {
          analytics.events.minimizePopup(id, modal.code);
          return prev.map((m) => (m.id === id ? { ...m, isMinimized: true } : m));
        }
        return prev;
      });

      // Salvo nel localStorage almeno se venisse refreshata la pagina apparirà di nuovo
      addStoredMinimizedPopup(id);
    },
    [setModals]
  );

  const maximize = React.useCallback(
    (id: string) => {
      const element = getElementById(id);
      delete element.dataset.keepForLater;
      // Non è una best practice, migliorare se possibile, ma non volevo mettere una referenza a modals per recuperare il code del popup
      setModals((prev) => {
        const modal = prev.find((m) => m.id === id);
        if (modal?.isMinimized) {
          analytics.events.maximizePopup(id, modal.code);
          return prev.map((m) => (m.id === id ? { ...m, isMinimized: false } : m));
        }
        return prev;
      });

      // Sposto l'id alla fine della coda per far scattare l'useEffect per la visualizzazione, in ModalContext.tsx
      setIds((prev) => {
        const filtered = prev.filter((existingId) => existingId !== id);
        return [...filtered, id];
      });
    },
    [setModals, setIds]
  );

  return { addEventListener, close, closeByRef, maximize, minimize, showByRef, showConfirm, showModal, showOffcanvas };
};

const isModal = (element: HTMLElement) => {
  return element.classList.contains('modal');
};

const isOffcanvas = (element: HTMLElement) => {
  return element.classList.contains('offcanvas');
};

const getType = (element: HTMLElement): 'modal' | 'offcanvas' => {
  if (isModal(element)) {
    return 'modal';
  }
  if (isOffcanvas(element)) {
    return 'offcanvas';
  } else {
    throw new Error('Cannot detect modal type');
  }
};

export const getElementById = (id: string) => {
  const element = document.getElementById(id);
  if (!element) {
    throw new Error(`Modal/Offcanvas with id ${id} does not exists.`);
  }
  return element;
};

export const getInstanceById = (id: string) => {
  const element = getElementById(id);
  if (isModal(element)) {
    const instance = bootstrap5.Modal.getOrCreateInstance(element);
    return instance;
  } else if (isOffcanvas(element)) {
    const instance = bootstrap5.Offcanvas.getOrCreateInstance(element);
    return instance;
  }
};
